// Theme persistence and resolution. No React in here on purpose — the same three
// lines of logic also run as the inline boot script in web/index.html, and the
// two must never drift.
//
// THREE MODES, TWO OUTCOMES:
//   'system'  follow prefers-color-scheme, now and whenever the OS changes
//   'light'   explicit, persisted, ignores the OS
//   'dark'    explicit, persisted, ignores the OS
// The *mode* is what we store; the *resolved theme* is always 'light' or 'dark'.
//
// 'system' is stored as the absence-equivalent value: readMode() treats anything
// that is not exactly 'light' or 'dark' as 'system', so a first-time visitor, a
// cleared key and a corrupted key all land on "follow the OS" rather than on a
// hardcoded light page.

/** Shared with the inline boot script in web/index.html. Change both together. */
export const THEME_STORAGE_KEY = 'crm.theme';

export const SYSTEM = 'system';
export const LIGHT = 'light';
export const DARK = 'dark';

const DARK_QUERY = '(prefers-color-scheme: dark)';

/** matchMedia is missing in jsdom and in some embedded webviews. */
function darkMediaQuery() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return null;
  return window.matchMedia(DARK_QUERY);
}

/** What the operating system currently wants. Defaults to light when unknown. */
export function systemPrefersDark() {
  return darkMediaQuery()?.matches ?? false;
}

/**
 * The stored mode. Storage throws rather than returning null in a few
 * locked-down contexts (Safari private browsing, some managed-device policies),
 * so every access is guarded — a browser that refuses to persist should still
 * render, it just will not remember.
 */
export function readMode() {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    return stored === LIGHT || stored === DARK ? stored : SYSTEM;
  } catch {
    return SYSTEM;
  }
}

export function writeMode(mode) {
  try {
    // 'system' is written out rather than removed so the value is legible when
    // someone is staring at devtools wondering why the page is not listening.
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    /* Not persisting is survivable; failing to render is not. */
  }
}

/** mode -> the theme actually shown. */
export function resolveTheme(mode) {
  if (mode === LIGHT || mode === DARK) return mode;
  return systemPrefersDark() ? DARK : LIGHT;
}

/**
 * Put the resolved theme on <html>. This is the same pair of writes the boot
 * script performs, so re-applying an unchanged theme is a no-op.
 *
 * `color-scheme` is set inline as well as via CSS because it governs things CSS
 * classes cannot reach on the first frame — scrollbar colour, native form
 * controls, and the canvas the browser paints outside the document.
 */
export function applyTheme(theme) {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark', theme === DARK);
  root.style.colorScheme = theme === DARK ? DARK : LIGHT;
}

/**
 * Call `onChange` when the OS preference flips. Returns an unsubscribe function.
 * Only meaningful while the mode is 'system' — the caller decides that, not us.
 */
export function subscribeToSystemTheme(onChange) {
  const query = darkMediaQuery();
  if (!query) return () => {};
  const handler = (event) => onChange(event.matches ? DARK : LIGHT);
  query.addEventListener('change', handler);
  return () => query.removeEventListener('change', handler);
}
